import { captureElement } from './captureElement.js';
import { exportElementPdf } from './exportElementPdf.js';
import { exportElementPng } from './exportElementPng.js';
import { freezeExportCaptureMotion } from './freezeExportCapture.js';
import {
  FullPageExportStageError,
  type FullPageExportRequest,
  type FullPageExportStage,
} from './types.js';

function rethrowAsStageError(stage: FullPageExportStage, err: unknown): never {
  if (err instanceof FullPageExportStageError) {
    throw err;
  }
  const message = err instanceof Error ? err.message : String(err);
  throw new FullPageExportStageError(stage, message, { cause: err });
}

/**
 * Capture the element once (budget-safe bands when needed), then share/download PDF or PNG.
 * Motion freeze is restored in `finally` even when encode/share throws.
 * Failures are tagged with {@link FullPageExportStageError.stage} for upstream diagnostics.
 */
export async function exportFullPage(request: FullPageExportRequest): Promise<void> {
  const restoreMotion = freezeExportCaptureMotion(request.element);
  try {
    let canvases: HTMLCanvasElement[];
    try {
      canvases = await captureElement(request.element);
    } catch (err) {
      rethrowAsStageError('capture', err);
    }

    if (request.format === 'pdf') {
      try {
        await exportElementPdf(canvases, request.filenameBase, request.delivery);
      } catch (err) {
        rethrowAsStageError('encode-pdf', err);
      }
      return;
    }

    try {
      await exportElementPng(canvases, request.filenameBase, request.delivery);
    } catch (err) {
      rethrowAsStageError('encode-png', err);
    }
  } finally {
    restoreMotion();
  }
}
